import React, { useMemo, useCallback } from 'react';
import type { Node, Edge } from '@xyflow/react';
import type { GapDetectionResult, GapItem } from '../../types/playbook.types';

interface AICanvasSuggestionsProps {
  gapResult: GapDetectionResult | null;
  nodes: Node[];
  edges: Edge[];
  onAcceptGap: (gap: GapItem) => void;
  onDismissGap: (index: number) => void;
}

const GHOST_NODE_STYLE = {
  border: '2px dashed #8b5cf6',
  opacity: 0.5,
  background: 'rgba(139, 92, 246, 0.05)',
  borderRadius: '8px',
};

const GHOST_BADGE_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: -8,
  right: -8,
  width: 16,
  height: 16,
  borderRadius: '50%',
  background: '#8b5cf6',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  zIndex: 10,
};

/**
 * Returns ghost nodes/edges to overlay on the React Flow canvas from gap detection.
 * Ghost nodes represent AI-suggested additions (new guards, missing transitions, etc.).
 */
export function useAIGhostElements(
  gapResult: GapDetectionResult | null,
  existingNodes: Node[],
): { ghostNodes: Node[]; ghostEdges: Edge[] } {
  return useMemo(() => {
    if (!gapResult || gapResult.gaps.length === 0) return { ghostNodes: [], ghostEdges: [] };

    const ghostNodes: Node[] = [];
    const ghostEdges: Edge[] = [];
    const existingNodeIds = new Set(existingNodes.map((n) => n.id));

    // Find the rightmost existing node for positioning ghost nodes
    let maxX = 0;
    for (const n of existingNodes) {
      if (n.position.x > maxX) maxX = n.position.x;
    }

    let ghostIndex = 0;
    for (const gap of gapResult.gaps) {
      if (gap.type === 'dead_end_status' && gap.suggestedFix?.action === 'add_transition') {
        // Suggest a new transition from the dead-end to a logical target
        const sourceSlug = gap.affectedEntity.slug;
        const sourceNode = existingNodes.find((n) => (n.data as any)?.slug === sourceSlug);
        if (sourceNode) {
          const ghostId = `ai-ghost-target-${ghostIndex}`;
          if (!existingNodeIds.has(ghostId)) {
            ghostNodes.push({
              id: ghostId,
              type: 'statusNode',
              position: { x: sourceNode.position.x + 300, y: sourceNode.position.y },
              data: {
                label: 'AI Suggested',
                slug: `suggested-${ghostIndex}`,
                category: 'in_progress',
                statusId: ghostId,
                isGhost: true,
                gapIndex: ghostIndex,
              },
              style: GHOST_NODE_STYLE,
            });
            ghostEdges.push({
              id: `ai-ghost-edge-${ghostIndex}`,
              source: sourceNode.id,
              target: ghostId,
              style: { stroke: '#8b5cf6', strokeDasharray: '6 3', opacity: 0.5, strokeWidth: 2 },
              animated: true,
            });
          }
        }
      } else if (gap.type === 'unreachable_status') {
        // Mark the unreachable status node with a ghost incoming edge
        const targetSlug = gap.affectedEntity.slug;
        const targetNode = existingNodes.find((n) => (n.data as any)?.slug === targetSlug);
        if (targetNode) {
          const ghostId = `ai-ghost-source-${ghostIndex}`;
          ghostNodes.push({
            id: ghostId,
            type: 'statusNode',
            position: { x: targetNode.position.x - 300, y: targetNode.position.y },
            data: {
              label: 'Connect from...',
              slug: `suggested-source-${ghostIndex}`,
              category: 'todo',
              statusId: ghostId,
              isGhost: true,
              gapIndex: ghostIndex,
            },
            style: GHOST_NODE_STYLE,
          });
          ghostEdges.push({
            id: `ai-ghost-edge-${ghostIndex}`,
            source: ghostId,
            target: targetNode.id,
            style: { stroke: '#8b5cf6', strokeDasharray: '6 3', opacity: 0.5, strokeWidth: 2 },
            animated: true,
          });
        }
      }
      ghostIndex++;
    }

    return { ghostNodes, ghostEdges };
  }, [gapResult, existingNodes]);
}

const AICanvasSuggestions: React.FC<AICanvasSuggestionsProps> = ({ gapResult, onAcceptGap, onDismissGap }) => {
  if (!gapResult || gapResult.gaps.length === 0) return null;

  const actionableGaps = gapResult.gaps.filter((g) => g.suggestedFix);

  if (actionableGaps.length === 0) return null;

  return (
    <div className="absolute bottom-3 left-3 z-10 max-w-[280px] rounded-lg border border-violet-200 dark:border-violet-700 bg-white/95 dark:bg-gray-800/95 shadow-lg p-2 space-y-1.5" style={{ animation: 'fadeSlideIn 200ms ease-out' }}>
      <div className="flex items-center justify-between">
        <span className="text-[9px] font-semibold text-violet-600 dark:text-violet-400 uppercase">AI Suggestions</span>
        <span className="text-[8px] text-gray-400">{actionableGaps.length} fix{actionableGaps.length !== 1 ? 'es' : ''}</span>
      </div>
      {actionableGaps.slice(0, 5).map((gap, i) => (
        <GapSuggestionCard key={i} gap={gap} index={i} onAccept={onAcceptGap} onDismiss={onDismissGap} />
      ))}
      {actionableGaps.length > 5 && (
        <p className="text-[8px] text-gray-400 text-center">+{actionableGaps.length - 5} more</p>
      )}
    </div>
  );
};

const GapSuggestionCard: React.FC<{
  gap: GapItem;
  index: number;
  onAccept: (gap: GapItem) => void;
  onDismiss: (index: number) => void;
}> = ({ gap, index, onAccept, onDismiss }) => {
  const handleAccept = useCallback(() => onAccept(gap), [gap, onAccept]);
  const handleDismiss = useCallback(() => onDismiss(index), [index, onDismiss]);

  return (
    <div className="flex items-start gap-1.5 rounded border border-violet-100 dark:border-violet-800 bg-violet-50/50 dark:bg-violet-900/10 p-1.5">
      <div className="flex-1 min-w-0">
        <p className="text-[8px] text-gray-600 dark:text-gray-400 truncate">{gap.description}</p>
        <p className="text-[7px] text-gray-400 dark:text-gray-500">
          {gap.affectedEntity.type}: {gap.affectedEntity.name}
        </p>
      </div>
      <div className="flex gap-0.5 shrink-0">
        <button type="button" onClick={handleAccept} className="text-[8px] text-emerald-600 hover:text-emerald-700 font-medium px-1">
          Apply
        </button>
        <button type="button" onClick={handleDismiss} className="text-[8px] text-gray-400 hover:text-gray-600">
          ×
        </button>
      </div>
    </div>
  );
};

export default AICanvasSuggestions;
